import { Content, Root, Trigger } from '@radix-ui/react-popover';
import { useId } from 'react';
import ColorSwatch from '../ColorSwatch';
import type { ColorPickerControlProps } from './ColorPickerControl.types';

const popoverContentClasses = [
  'tw:bg-sq-white tw:dark:bg-sq-dark-background',
  'tw:border tw:border-solid tw:rounded-[0.5rem] tw:border-sq-disabled-gray tw:dark:border-gray-500',
  'tw:relative tw:z-[1200] tw:min-w-6',
  'tw:focus-visible:outline-none tw:outline-none',
  'tw:data-[state=open]:animate-in tw:data-[state=closed]:animate-out',
  'tw:data-[side=top]:animate-slideDownAndFade tw:data-[side=right]:animate-slideLeftAndFade',
  'tw:data-[side=bottom]:animate-slideUpAndFade tw:data-[side=left]:animate-slideRightAndFade',
  'tw:text-sq-text-color tw:dark:text-sq-text-color-dark',
].join(' ');

function ColorPickerControl({
  color,
  label,
  children,
  placement = 'bottom',
  align = 'start',
  extraClassNames = '',
  id,
  testId,
  onOpen,
}: ColorPickerControlProps) {
  const swatchId = useId();

  const triggerLabel = (
    <label
      className="tw:cursor-pointer tw:whitespace-nowrap tw:text-sm tw:text-sq-text-color tw:dark:text-sq-text-color-dark"
      htmlFor={swatchId}>
      {label}
    </label>
  );

  const swatch = <ColorSwatch aria-label={label} color={color} id={id ?? swatchId} size="sm" onClick={onOpen} />;

  if (!children) {
    return (
      <div className={`tw:flex tw:items-center tw:gap-2 ${extraClassNames}`}>
        <span className="tw:cursor-pointer tw:whitespace-nowrap tw:text-sm tw:text-sq-text-color tw:dark:text-sq-text-color-dark">
          {label}
        </span>
        {swatch}
      </div>
    );
  }

  return (
    <div className={`tw:flex tw:items-center tw:gap-2 ${extraClassNames}`}>
      {triggerLabel}
      <Root>
        <Trigger asChild>{swatch}</Trigger>
        <Content align={align} className={popoverContentClasses} data-testid={testId} side={placement} sideOffset={2}>
          {children}
        </Content>
      </Root>
    </div>
  );
}

export default ColorPickerControl;
